import { useTranslation } from "react-i18next";
import { DowntimeMethodCapturesEntity } from "entities/DowntimeMethodCaptures/DowntimeMethodCapturesEntity";
import { Formik, Form } from "formik";
import { Box, Button, Tab, Tabs } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { IMachineCenterCreationFormValues, IMachineCenterCreationForm } from "./MachineCenterCreationFormInterfaces";
import { machineCenterCorporateBOFormSchema } from "validations/schemas";
import { useStyles } from "./MachineCenterCreationForm.styles";
import { DOWNTIMEMETHODCAPTURE } from "constants/constants";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { useCallback, useMemo, useState } from "react";
import { GeneralTab } from "./tabs/GeneralTab";
import { PerformanceTab } from "./tabs/PerformanceTab";

const TAB_INDEX_GENERAL: number = 0;
const TAB_INDEX_PERFORMANCE: number = 1;

export const MachineCenterCreationForm = ({
    handleSubmit,
    handleClose,
    apolloMutationError,
    isEdit,
    machineCenter,
    millId,
    machineCenterId,
    duplicateMachineCenterInfo,
    isMillAspenPlusEnabled = false,
}: IMachineCenterCreationForm) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const isDuplicationWithSelection = useMemo(() => Boolean(duplicateMachineCenterInfo), [duplicateMachineCenterInfo]);
    const [errorUniquePlcAddress, setErrorUniquePlcAddress] = useState("");
    const [machineCenterIdSelected, setMachineCenterIdSelected] = useState<number | undefined>(undefined);
    const [duplicationInCreation, setDuplicationInCreation] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState(TAB_INDEX_GENERAL);

    const { downtimeMethodCaptures } = DowntimeMethodCapturesEntity.GetDowntimeMethodCapture();
    const {
        machineCenterFailureDetails,
        productionLineOptions,
        loading,
        error: errorMachineCenterDuplicationDetails,
    } = MachineCentersEntity.GetMachineCenterFailureDetailsInfoBO(
        duplicateMachineCenterInfo?.machineCenterId || machineCenterIdSelected,
        duplicationInCreation ? millId : undefined,
    );

    const handleSubmitCallback = useCallback(
        async (values: IMachineCenterCreationFormValues) => {
            let checkUniquenessPlcAddress = values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC;
            if (checkUniquenessPlcAddress) {
                const uniquePlcAddress = await MachineCentersEntity.uniquePlcAddressByMill(
                    millId,
                    values.plc_address,
                    machineCenterId,
                );
                if (uniquePlcAddress.unique) {
                    setErrorUniquePlcAddress("");
                    await handleSubmit(values);
                } else {
                    setErrorUniquePlcAddress(
                        t(
                            "corporate_backoffice.production_line_view.machine_center_creation_form.unique_plc_address_error_message",
                            { name: uniquePlcAddress.machineCenterName },
                        ),
                    );
                }
            } else {
                if ((isDuplicationWithSelection || duplicationInCreation) && machineCenterFailureDetails[0]) {
                    await handleSubmit(values, machineCenterFailureDetails[0]);
                } else {
                    await handleSubmit(values);
                }
            }
        },
        [
            handleSubmit,
            machineCenterId,
            millId,
            t,
            machineCenterFailureDetails,
            isDuplicationWithSelection,
            duplicationInCreation,
        ],
    );

    const errorMessage = useMemo(() => {
        if (apolloMutationError?.message.includes("uq_machine_center_name")) {
            return t(
                "corporate_backoffice.production_line_view.machine_center_creation_form.unique_name_machine_center_name",
            );
        } else if (Boolean(errorUniquePlcAddress)) {
            return errorUniquePlcAddress;
        } else if (apolloMutationError?.message.includes("uq_machine_center_plc_address_by_mill")) {
            return t(
                "corporate_backoffice.production_line_view.machine_center_creation_form.unique_plc_address_error_message_fixed",
            );
        } else if (Boolean(errorMachineCenterDuplicationDetails)) {
            return t(
                "corporate_backoffice.production_line_view.machine_center_creation_form.get_duplication_details_error",
            );
        } else {
            return t("setup_corporation.errors.creation_error");
        }
    }, [errorUniquePlcAddress, apolloMutationError, errorMachineCenterDuplicationDetails, t]);

    const initFormValues: IMachineCenterCreationFormValues = useMemo(
        () =>
            isEdit && machineCenter
                ? machineCenter
                : {
                      name: "",
                      short_name: "",
                      manufacturer: "",
                      model_number: "",
                      downtime_method_capture_id: "",
                      plc_address: "",
                      minimum_downtime: "",
                      downtime_trigger_threshold: "",
                      is_bottleneck: false,
                      aspen_plus_oee_enabled: false,
                      oee_input_method_id: "",
                      is_duplication_with_selection: Boolean(isDuplicationWithSelection),
                      is_duplication_without_selection: duplicationInCreation,
                      production_line_id_send_duplicate: duplicateMachineCenterInfo?.productionLineId || "",
                      machine_center_id_to_duplicate: "",
                      production_line_id_to_duplicate: "",
                  },
        [duplicateMachineCenterInfo, isDuplicationWithSelection, duplicationInCreation, isEdit, machineCenter],
    );

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={machineCenterCorporateBOFormSchema}
            onSubmit={handleSubmitCallback}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                const handleTabChange = (_event: React.SyntheticEvent, newTab: number) => setActiveTab(newTab);

                return (
                    <Form className={classes.formContainer}>
                        <Box className={classes.tabsHeader}>
                            <Tabs value={activeTab} onChange={handleTabChange} className={classes.tabs}>
                                <Tab
                                    value={TAB_INDEX_GENERAL}
                                    label={t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.tab_general",
                                    )}
                                />
                                {isMillAspenPlusEnabled && values.aspen_plus_oee_enabled && (
                                    <Tab
                                        value={TAB_INDEX_PERFORMANCE}
                                        label={t(
                                            "corporate_backoffice.production_line_view.machine_center_creation_form.tab_performance",
                                        )}
                                    />
                                )}
                            </Tabs>
                        </Box>
                        {activeTab === TAB_INDEX_GENERAL && (
                            <GeneralTab
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                setFieldValue={setFieldValue}
                                t={t}
                                isEdit={isEdit}
                                isDuplicationWithSelection={isDuplicationWithSelection}
                                duplicationInCreation={duplicationInCreation}
                                setDuplicationInCreation={setDuplicationInCreation}
                                duplicateMachineCenterInfo={duplicateMachineCenterInfo}
                                productionLineOptions={productionLineOptions}
                                downtimeMethodCaptures={downtimeMethodCaptures}
                                isMillAspenPlusEnabled={isMillAspenPlusEnabled}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                setMachineCenterIdSelected={setMachineCenterIdSelected}
                            />
                        )}
                        {activeTab === TAB_INDEX_PERFORMANCE &&
                            isMillAspenPlusEnabled &&
                            values.aspen_plus_oee_enabled && (
                                <PerformanceTab
                                    values={values}
                                    errors={errors}
                                    touched={touched}
                                    setFieldValue={setFieldValue}
                                    t={t}
                                    isMillAspenPlusEnabled={isMillAspenPlusEnabled}
                                />
                            )}
                        <p
                            style={{
                                display:
                                    apolloMutationError ||
                                    Boolean(errorUniquePlcAddress) ||
                                    Boolean(errorMachineCenterDuplicationDetails)
                                        ? "block"
                                        : "none",
                                color: "red",
                            }}
                        >
                            {errorMessage}
                        </p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.cancelButton",
                                )}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting || loading || Boolean(errorMachineCenterDuplicationDetails)}
                            >
                                {isEdit ? (
                                    t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.edit_button",
                                    )
                                ) : (
                                    <>
                                        <AddIcon />{" "}
                                        {t(
                                            "corporate_backoffice.production_line_view.machine_center_creation_form.addButton",
                                        )}
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
